import { useState, useEffect, useCallback } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  TablePagination
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import useMounted from '../../../hooks/useMounted';
import { dashboardApi } from '../../../api/dashboardApi';
import { getTone, toneAlpha, grey, lightVeil, darkVeil } from '../../../theme/tones';

// Helper to generate initials avatar colors dynamically
function stringToColor(string) {
  let hash = 0;
  let i;
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function stringAvatar(firstname, lastname) {
  const f = firstname ? firstname.trim() : '';
  const l = lastname ? lastname.trim() : '';
  const fullName = `${f} ${l}`.trim() || 'Commercial';
  
  // Calculate initials
  let initials = 'C';
  if (f && l) {
    initials = `${f[0]}${l[0]}`;
  } else if (f) {
    initials = f.slice(0, 2);
  } else if (l) {
    initials = l.slice(0, 2);
  }
  
  return {
    sx: {
      bgcolor: stringToColor(fullName),
      fontWeight: 700,
      fontSize: '0.85rem',
      width: 38,
      height: 38,
      boxShadow: `0 2px 8px ${darkVeil(0.15)}`
    },
    children: initials.toUpperCase()
  };
}

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value || 0);
};

const CommercialsRevenueTable = () => {
  const theme = useTheme();
  const mounted = useMounted();
  
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Year & Month filter state
  const [year, setYear] = useState('2026');
  const [month, setMonth] = useState('all');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const fetchCommercialRevenue = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const data = await dashboardApi.getCommercialRevenue(token, year, month);
      
      if (mounted.current) {
        setCommercials(data || []);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching commercial revenue:', err);
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [year, month, mounted]);

  useEffect(() => {
    fetchCommercialRevenue();
  }, [fetchCommercialRevenue]);

  const handleYearChange = (event) => {
    setYear(event.target.value);
    setPage(0);
  };

  const handleMonthChange = (event) => {
    setMonth(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter commercials based on local search query
  const filteredCommercials = commercials.filter((comm) => {
    const fullName = `${comm.firstname || ''} ${comm.lastname || ''}`.toLowerCase();
    const email = (comm.email || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  const paginatedCommercials = filteredCommercials.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Card
      sx={{
        borderRadius: '16px',
        boxShadow: theme.palette.mode === 'dark'
          ? `0 10px 30px -5px ${darkVeil(0.6)}`
          : `0 10px 25px -5px ${darkVeil(0.05)}`,
        border: theme.palette.mode === 'dark'
          ? `1px solid ${lightVeil(0.05)}`
          : `1px solid ${darkVeil(0.05)}`,
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${toneAlpha('neutral', 0.8)} 0%, ${toneAlpha('brand', 0.85)} 100%)`
          : grey(0),
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
      }}
    >
      <CardHeader
        sx={{
          p: 3,
          alignItems: 'center',
          flexDirection: { xs: 'column', md: 'row' },
          '& .MuiCardHeader-action': {
            margin: 0,
            alignSelf: 'center',
            width: { xs: '100%', md: 'auto' }
          }
        }}
        title={
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Chiffre d'affaires par commercial
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Suivi en temps réel des ventes nettes et brutes par conseiller
            </Typography>
          </Box>
        }
        action={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'space-between', md: 'flex-end' },
              flexWrap: 'wrap',
              gap: 2,
              mt: { xs: 2, md: 0 },
              width: '100%'
            }}
          >
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: '8px', height: 38 }
              }}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />

            {/* Year Dropdown */}
            <FormControl size="small" sx={{ width: { xs: '48%', sm: 100 } }}>
              <InputLabel id="select-year-label">Année</InputLabel>
              <Select
                labelId="select-year-label"
                id="select-year"
                value={year}
                label="Année"
                onChange={handleYearChange}
                sx={{ borderRadius: '8px', height: 38 }}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="2026">2026</MenuItem>
                <MenuItem value="2025">2025</MenuItem>
                <MenuItem value="2024">2024</MenuItem>
                <MenuItem value="2023">2023</MenuItem>
              </Select>
            </FormControl>

            {/* Month Dropdown */}
            <FormControl size="small" sx={{ width: { xs: '48%', sm: 130 } }}>
              <InputLabel id="select-month-label">Mois</InputLabel>
              <Select
                labelId="select-month-label"
                id="select-month"
                value={month}
                label="Mois"
                onChange={handleMonthChange}
                sx={{ borderRadius: '8px', height: 38 }}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="1">Janvier</MenuItem>
                <MenuItem value="2">Février</MenuItem>
                <MenuItem value="3">Mars</MenuItem>
                <MenuItem value="4">Avril</MenuItem>
                <MenuItem value="5">Mai</MenuItem>
                <MenuItem value="6">Juin</MenuItem>
                <MenuItem value="7">Juillet</MenuItem>
                <MenuItem value="8">Août</MenuItem>
                <MenuItem value="9">Septembre</MenuItem>
                <MenuItem value="10">Octobre</MenuItem>
                <MenuItem value="11">Novembre</MenuItem>
                <MenuItem value="12">Décembre</MenuItem>
              </Select>
            </FormControl>
          </Box>
        }
      />
      
      <Divider />

      <TableContainer>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.mode === 'dark'
                  ? lightVeil(0.02)
                  : darkVeil(0.01)
              }}
            >
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Commercial</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Chiffre d'Affaires Net</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Chiffre d'Affaires Brut</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Chargement des performances...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedCommercials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun commercial trouvé pour ces critères.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCommercials.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? lightVeil(0.03)
                        : darkVeil(0.015)
                    }
                  }}
                >
                  {/* Commercial details (Avatar, Name, Email) */}
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={row.avatar ? `/uploads/avatars/${row.avatar}` : undefined}
                        {...stringAvatar(row.firstname, row.lastname)}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {row.firstname} {row.lastname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* CA Net */}
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 750, color: 'success.main' }}>
                        {formatCurrency(row.ca_net)}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: theme.palette.mode === 'dark'
                            ? toneAlpha('brandGreen', 0.15)
                            : toneAlpha('brandGreen', 0.08),
                          color: theme.palette.mode === 'dark' ? getTone('emerald').light : getTone('brandGreen').deep,
                          borderRadius: '16px',
                          padding: '2px 10px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          border: `1px solid ${theme.palette.mode === 'dark' ? toneAlpha('emerald', 0.2) : toneAlpha('brandGreen', 0.15)}`
                        }}
                      >
                        {row.count_net} {row.count_net > 1 ? 'ventes' : 'vente'}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* CA Brut */}
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {formatCurrency(row.ca_brut)}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: theme.palette.mode === 'dark'
                            ? lightVeil(0.06)
                            : darkVeil(0.04),
                          color: 'text.secondary',
                          borderRadius: '16px',
                          padding: '2px 10px',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          border: `1px solid ${theme.palette.mode === 'dark' ? lightVeil(0.1) : darkVeil(0.05)}`
                        }}
                      >
                        {row.count_brut} {row.count_brut > 1 ? 'total' : 'total'}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />
      
      <TablePagination
        component="div"
        count={filteredCommercials.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Lignes par page:"
        sx={{
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.8rem',
            color: 'text.secondary'
          }
        }}
      />
    </Card>
  );
};

export default CommercialsRevenueTable;
